import { forwardRef, useImperativeHandle, useState } from 'react';
import dayjs from 'dayjs';
import { CardErrorsByField } from '@components/forms/CreditCardForm/utils';
import { PatientCreditCard } from '@qmd-types/patient';
import CreditCardsList from './CreditCardsList';
import AddCardPopUp from './AddCardPopUp';
import { Portal } from 'react-portal';
import {
  cn,
  CreditCardIcon,
  EditIcon,
  ErrorMessage,
  Field,
  Paragraph,
} from '@quickmd-llc/design-system';
import { useMht } from '@pages/SchedulePage/hooks/useMht';

interface Props {
  cardErrors?: CardErrorsByField;
  creditCards: PatientCreditCard[];
  selectedCreditCard: PatientCreditCard;
  setSelectedCreditCard: (creditCard: PatientCreditCard) => void;
}
export interface RefProps {
  close: () => void;
}

const CreditCardPicker = forwardRef<RefProps, Props>(
  (
    {
      cardErrors,
      creditCards,
      selectedCreditCard,
      setSelectedCreditCard,
    }: Props,
    ref,
  ): JSX.Element => {
    const [showCardList, setShowCardList] = useState<boolean>(false);
    const [showAddCard, setShowAddCard] = useState<boolean>(false);

    useImperativeHandle(
      ref,
      () => ({
        close: () => {
          setShowCardList(false);
          setShowAddCard(false);
        },
      }),
      [],
    );

    const generalErrors = cardErrors?.general;

    function handleShowCardList() {
      setShowCardList(true);
    }

    function handleCloseCardList() {
      setShowCardList(false);
    }

    function handleShowAddCard() {
      setShowAddCard(true);
    }

    function handleCloseAddCard() {
      setShowAddCard(false);
    }

    const { isMht } = useMht();

    return (
      <div>
        <Paragraph
          className={cn('font-semibold text-text-dark', {
            'text-text-error': generalErrors?.length,
          })}
        >
          Payment method
        </Paragraph>
        {isMht && (
          <Paragraph size="sm" className="mb-sp-1 text-text-medium">
            You must enter payment method to begin membership.
          </Paragraph>
        )}

        {generalErrors && <Field>{generalErrors.map(mapError)}</Field>}

        <div className="mt-2 flex flex-row justify-between rounded-lg bg-white p-4">
          <div className="flex h-auto flex-row gap-2 self-center">
            <CreditCardIcon
              type={selectedCreditCard.type}
              iconSize="l"
              className="text-text-dark"
            />
            <Paragraph size="sm" className="font-semibold text-text-dark">
              {selectedCreditCard.number.slice(4)}
            </Paragraph>
            <Paragraph size="xs" className="self-center text-neutral-70">
              {dayjs(selectedCreditCard.expiry, 'YYYY-MM').format('MM/YY')}
            </Paragraph>
          </div>
          <EditIcon
            className="cursor-pointer text-text-dark"
            iconSize="l"
            onClick={handleShowCardList}
          />
        </div>

        {(showCardList || showAddCard) && (
          <Portal>
            <div
              className={cn(
                'absolute bottom-0 left-0 right-0 top-0 z-50 flex h-full w-full flex-1 justify-center bg-gray-500 bg-opacity-50',
                { 'p-4': showAddCard },
              )}
            >
              {showAddCard ? (
                <AddCardPopUp
                  handleClose={handleCloseAddCard}
                  setSelectedCreditCard={setSelectedCreditCard}
                />
              ) : (
                <CreditCardsList
                  creditCards={creditCards}
                  handleClose={handleCloseCardList}
                  selectedCreditCardId={selectedCreditCard.id}
                  setSelectedCreditCard={setSelectedCreditCard}
                  handleAddCard={handleShowAddCard}
                />
              )}
            </div>
          </Portal>
        )}
      </div>
    );
  },
);
const mapError = (error: string) => (
  <ErrorMessage key={error}>{error}</ErrorMessage>
);
export default CreditCardPicker;
